import React from 'react';
import {
  Box,
  TextField,
  Button,
  Stack,
  MenuItem,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { Client } from '../../../types';
import { updateClient } from '../../../services/firebase';
import { countries } from '../../../utils/countries';

interface EditClientFormProps {
  client: Client;
  onSuccess: (client: Client) => void;
  onCancel?: () => void;
}

interface FormInputs {
  fullName: string;
  email: string;
  phoneNumber: string;
  countryOfOrigin: string;
}

const prettifyPhone = (raw: string): string => {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return raw;
};

const EditClientForm: React.FC<EditClientFormProps> = ({ client, onSuccess, onCancel }) => {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormInputs>({
    defaultValues: {
      fullName: client.fullName,
      email: client.email,
      phoneNumber: prettifyPhone(client.phoneNumber),
      countryOfOrigin: client.countryOfOrigin as string,
    },
  });

  const onSubmit = async (data: FormInputs) => {
    try {
      const updatedClient = await updateClient(client.id, data);
      onSuccess(updatedClient);
    } catch (error) {
      console.error('Error updating client:', error);
    }
  };

  const labelSx = {
    display: 'block',
    fontSize: '0.8125rem',
    fontWeight: 500,
    color: 'text.primary',
    mb: 1,
  };

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          <Box>
            <Typography component="label" htmlFor="edit-full-name" sx={labelSx}>
              Full Name
            </Typography>
            <Controller
              name="fullName"
              control={control}
              rules={{ required: 'Full name is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  id="edit-full-name"
                  fullWidth
                  error={!!errors.fullName}
                  helperText={errors.fullName?.message}
                />
              )}
            />
          </Box>

          <Box>
            <Typography component="label" htmlFor="edit-email" sx={labelSx}>
              Email Address
            </Typography>
            <Controller
              name="email"
              control={control}
              rules={{
                required: 'Email is required',
                pattern: { value: /\S+@\S+\.\S+/, message: 'Invalid email format' },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  id="edit-email"
                  type="email"
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
              )}
            />
          </Box>

          <Box>
            <Typography component="label" htmlFor="edit-phone" sx={labelSx}>
              Phone Number
            </Typography>
            <Controller
              name="phoneNumber"
              control={control}
              rules={{
                required: 'Phone number is required',
                pattern: {
                  value: /^\+?[\d\s-]+$/,
                  message: 'Invalid phone number format',
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  id="edit-phone"
                  fullWidth
                  placeholder="+1-555-123-4567"
                  error={!!errors.phoneNumber}
                  helperText={
                    errors.phoneNumber?.message ||
                    'Format: +1-123-456-7890 or 123-456-7890'
                  }
                />
              )}
            />
          </Box>

          <Box>
            <Typography component="label" htmlFor="edit-country" sx={labelSx}>
              Country of Origin
            </Typography>
            <Controller
              name="countryOfOrigin"
              control={control}
              rules={{ required: 'Country of origin is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  id="edit-country"
                  select
                  fullWidth
                  error={!!errors.countryOfOrigin}
                  helperText={errors.countryOfOrigin?.message}
                >
                  {countries.map((country) => (
                    <MenuItem key={country.code} value={country.name}>
                      {country.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1.5,
              pt: 1,
            }}
          >
            {onCancel && (
              <Button
                type="button"
                variant="text"
                onClick={onCancel}
                disabled={isSubmitting}
                sx={{ minHeight: 40 }}
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              sx={{ minHeight: 40, minWidth: 160 }}
              startIcon={
                isSubmitting ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : null
              }
            >
              {isSubmitting ? 'Saving…' : 'Save Changes'}
            </Button>
          </Box>
        </Stack>
      </form>
    </Box>
  );
};

export default EditClientForm;
